/**
 * Karte „Nachfass nach dem Webinar je Publisher" (P4). Persona-Umbau A1 (Plan 2026-10-28, Tills Kritik: „was soll Marketing
 * daraus lesen?"): Hauptwert ist der Erwartete Wert je 1.000 Empfänger — dieselbe Rechnung wie die Nachfass-Zeile von P3
 * „Direkte Bestell-Links" (Backend `nachfassAnfosSql` mit `anfoWertVorZahlungSql`, Test H8: Summe = P3-Zeile). Daneben Anfos
 * je 1.000 Empfänger. Publisher unter der Grau-Schwelle stehen in „übrige Listen". Die Abdeckung steht nur noch als
 * Warnzeile, wenn eine Liste unter 90 % fällt. Befund mit dem Heat-Up-Vergleich (P3 „Webinar-Einladungen") und einer
 * Schätzung für die Webinare ohne Nachfass. Backend: /api/auswertungen/hebel/nachfass?monate=3|6|12.
 */
import { FC, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Link, useMediaQuery, useTheme } from '@mui/material';
import ChartCard from '../../../components/auswertungen/ChartCard';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { AnfosZelle, BefundZeile, Hinweis, Laden, int, jeTausend, vorlaeufigSatz } from '../../../components/auswertungen/kennzahlTeile';
import type { KanalFensterMonate } from '../../../helpers/auswertungenData';
import type { NachfassAntwort } from '../../../helpers/hebelData';
import { ATTRIBUTION, formatPct, GLOSSAR, TERMS } from '../../../helpers/kpiDefinitions';
import { germanNow } from '../../../helpers/germanTime';
import { abdeckungWarnung, euroJe1000, nachfassBefund, nachfassGruppen, type NachfassGruppe } from './befund';
import { KartenFenster, tag, Unterzeile } from './teile';

const minusTag = (v: string): string => {
  const [j, m, t] = v.split('-').map(Number);
  const d = new Date(Date.UTC(j, m - 1, t - 1));
  return d.toISOString().slice(0, 10);
};

/** Grenze der Abdeckungs-Warnung (Plan A1). */
export const ABDECKUNG_WARNGRENZE = 90;

/** Eingabe des Befunds aus der Antwort — auch für den Kopf der Seite (dieselbe Rechnung). */
export const nachfassBefundEingabe = (data: NachfassAntwort) => ({
  anfos: data.summe.anfos,
  anfos_vorlaeufig: data.summe.anfos_vorlaeufig,
  wert_je_1000: data.summe.wert_je_1000,
  wert_je_anfo: data.summe.wert_je_anfo,
  heatup_wert_je_1000: data.heatup?.wert_je_1000 ?? null,
  webinare_im_fenster: data.webinare.im_fenster,
  webinare_mit_nachfass: data.webinare.mit_nachfass,
  ohne_nachfass: data.webinare.ohne_nachfass,
});

/** D16: Standardspalten je Kartenbreite — am Handy Publisher und Hauptwert (wie bisher fest verdrahtet), wählbar im Menü. */
const STANDARDS: Record<Breite, readonly string[]> = {
  breit: ['publisher', 'wert', 'anfos_je_1000', 'anfos', 'empfaenger'],
  mittel: ['publisher', 'wert', 'anfos_je_1000', 'anfos', 'empfaenger'],
  schmal: ['publisher', 'wert'],
};

const NachfassKarte: FC<{
  data: NachfassAntwort | null;
  fehler: string | null;
  monate: KanalFensterMonate;
  onMonate: (m: KanalFensterMonate) => void;
  /** Neue Daten laden, die alten stehen noch (die Sortierung der Tabelle bleibt erhalten). */
  laedt?: boolean;
}> = ({ data, fehler, monate, onMonate, laedt = false }) => {
  const theme = useTheme();
  // Bei 390 px lag der Hauptwert außerhalb des Bildes (Darstellungsprüfung Runde 2).
  const schmal = useMediaQuery(theme.breakpoints.down('sm'));
  const schwelle = data?.grau_schwellen.anfos ?? 20;
  const s = data?.summe;
  const grau = (r: { wenig_daten: boolean }) => (r.wenig_daten ? `unter ${schwelle} Anfos — die Quote kann am Zufall hängen` : null);
  const zeilen = useMemo(() => (data ? nachfassGruppen(data.zeilen, schwelle) : []), [data, schwelle]);
  const warnung = useMemo(
    () => (data ? abdeckungWarnung(data.zeilen, ABDECKUNG_WARNGRENZE, data.grau_schwellen.anmeldungen) : []),
    [data],
  );

  const spalten: Spalte<NachfassGruppe>[] = [
    {
      key: 'publisher',
      pflicht: true,
      label: TERMS.publisher.label,
      tip: `${TERMS.publisher.tip} ${TERMS.nachfassUebrige.label}: ${TERMS.nachfassUebrige.tip}`,
      minWidth: 120,
      render: (r) => (
        <Box sx={{ lineHeight: 1.3, whiteSpace: 'normal' }}>
          <Box component="span" sx={{ fontWeight: r.bekannt || r.uebrige ? 600 : 400 }}>
            {r.label}
          </Box>
          {r.unter && <Unterzeile>{r.unter}</Unterzeile>}
        </Box>
      ),
      sortValue: (r) => r.label,
    },
    // Reihenfolge nach Gewicht (Runde 2, Marketing): erst der Hauptwert, die Mengen dahinter; schmal nur der Hauptwert.
    {
      key: 'wert',
      label: TERMS.nachfassWertJe1000.short,
      tip: TERMS.nachfassWertJe1000.tip,
      einheit: 'Erwarteter Wert, netto',
      align: 'right',
      // Hauptwert (Tills Kritik): fett und größer, direkt neben dem Publisher; schmal mit Anfos darunter.
      render: (r) => (
        <Box sx={{ lineHeight: 1.25 }}>
          <Box component="span" sx={{ fontWeight: 700, fontSize: 15 }}>
            {euroJe1000(r.wert_je_1000)}
          </Box>
          {schmal && (
            <Unterzeile>
              {int(r.anfos)} Anfos · {jeTausend(r.anfos_je_1000)} je 1.000
            </Unterzeile>
          )}
        </Box>
      ),
      sortValue: (r) => r.wert_je_1000,
      unsicher: grau,
      summary: s ? (
        <Box sx={{ lineHeight: 1.25 }}>
          <Box component="span" sx={{ fontWeight: 700, fontSize: 15 }}>
            {euroJe1000(s.wert_je_1000)}
          </Box>
          {schmal && (
            <Unterzeile>
              {int(s.anfos)} Anfos · {jeTausend(s.anfos_je_1000)} je 1.000
            </Unterzeile>
          )}
        </Box>
      ) : undefined,
    },
    {
      key: 'anfos_je_1000',
      label: TERMS.nachfassAnfosJe1000.short,
      tip: TERMS.nachfassAnfosJe1000.tip,
      einheit: 'Anfos je 1.000 Empf.',
      align: 'right',
      render: (r) => jeTausend(r.anfos_je_1000),
      sortValue: (r) => r.anfos_je_1000,
      unsicher: grau,
      summary: s ? jeTausend(s.anfos_je_1000) : undefined,
    },
    {
      key: 'anfos',
      label: TERMS.nachfassAnfos.short,
      tip: TERMS.nachfassAnfos.tip,
      align: 'right',
      render: (r) => <AnfosZelle n={r.anfos} vorlaeufig={r.anfos_vorlaeufig} />,
      sortValue: (r) => r.anfos,
      summary: s ? <AnfosZelle n={s.anfos} vorlaeufig={s.anfos_vorlaeufig} /> : undefined,
    },
    {
      key: 'empfaenger',
      label: TERMS.nachfassEmpfaenger.label,
      tip: TERMS.nachfassEmpfaenger.tip,
      align: 'right',
      render: (r) => int(r.empfaenger),
      sortValue: (r) => r.empfaenger,
      summary: s ? int(s.empfaenger) : undefined,
    },
  ];

  const meta = spaltenMeta(spalten);
  const wahl = useSpaltenwahl('hebelNachfass', meta, STANDARDS);
  const sichtbar = new Set(wahl.sichtbar);

  const befund = data ? nachfassBefund(nachfassBefundEingabe(data), schwelle, germanNow().format('YYYY-MM-DD')) : null;

  return (
    <ChartCard
      title="Nachfass nach dem Webinar je Publisher"
      actions={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <SpaltenMenue spalten={meta} wahl={wahl} />
          <KartenFenster monate={monate} onChange={onMonate} />
        </Box>
      }
      howToRead={
        <>
          Was der Nachfass je 1.000 Empfänger erwarten lässt, je Liste. Listen mit wenigen Anfos stehen zusammen unter „übrige
          Listen“ — dort hängt jede Quote an ein, zwei Anfos.
        </>
      }
      terms={[
        { label: TERMS.nachfassWertJe1000.label, tip: TERMS.nachfassWertJe1000.tip },
        { label: TERMS.heatUpVergleich.label, tip: TERMS.heatUpVergleich.tip },
        { label: TERMS.nachfassOhne.label, tip: TERMS.nachfassOhne.tip },
        { label: ATTRIBUTION.bannerFlaeche.label, tip: ATTRIBUTION.bannerFlaeche.tip },
        { label: TERMS.nachfassEmpfaenger.label, tip: TERMS.nachfassEmpfaenger.tip },
      ]}
      source="Nachfass-Segmente mit ihren Adressen, PL-X-Bestellungen mit Nachfass-Kennung, Anfo-Werte"
      freshnessKey={['plx_orders', 'webinar_send_metrics']}
    >
      {!data ? (
        <Laden fehler={fehler} />
      ) : (
        <Box ref={wahl.messRef} sx={{ opacity: laedt ? 0.55 : 1, transition: 'opacity 120ms' }} aria-busy={laedt}>
          {befund && (
            <BefundZeile text={'text' in befund ? befund.text : undefined} fehlt={'fehlt' in befund ? befund.fehlt : null} tip={TERMS.nachfassOhne.tip} />
          )}
          <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
            Versände und Bestellungen {tag(data.window.von)}–{tag(minusTag(data.window.versand_bis))} ({data.window.monate} Monate;
            die letzten {data.reife_versand_tage} Tage zählen noch nicht) · {TERMS.webinarAbdeckungNachfass.label}{' '}
            {int(data.webinare.mit_nachfass)} von {int(data.webinare.im_fenster)}
            {/* Der Heat-Up-Vergleich steht schon im Befund darüber. */}
            {laedt && ' · lädt …'}
          </Box>
          {warnung.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              Abdeckung unter {ABDECKUNG_WARNGRENZE} % in Webinaren mit Nachfass:{' '}
              {warnung
                .map((r) => `${r.label} ${formatPct(r.abdeckung_mit_nachfass, 0)} (${int(r.im_segment_mit_nachfass)} von ${int(r.anmelder_mit_nachfass)} Anmeldern)`)
                .join(' · ')}
              . {TERMS.nachfassAbdeckungMitNachfass.tip}
            </Alert>
          )}
          <SortableTable
            rows={zeilen}
            columns={spalten.filter((c) => sichtbar.has(c.key))}
            rowKey={(r) => r.key}
            initialSort={{ key: 'wert', dir: 'desc' }}
            kinder={(r) => r.kinder}
            kinderText={(r) => (r.kinder ? `${r.kinder.length} Listen` : null)}
            muted={(r) => r.wenig_daten && !r.uebrige}
            summaryLabel="Alle Publisher"
            maxHeight={null}
            festeErsteSpalte
            eng
          />
          <Hinweis>
            Euro netto, Erwarteter Wert (bei Bestellung) — dieselbe Rechnung und dieselben Summen wie die Nachfass-Zeile von{' '}
            <Link component={RouterLink} to="/auswertungen/kanaele#direkte-bestell-links" underline="hover">
              Kanäle &amp; Werbeflächen → Direkte Bestell-Links
            </Link>{' '}
            ({int(data.summe.anfos)} Anfos, {int(data.summe.empfaenger)} Empfänger, {euroJe1000(data.summe.wert_je_1000)} je 1.000).
            Nenner sind die Empfänger: die Adressen der verschickten Nachfass-Gruppen, Stand beim letzten Abgleich vor der zweiten
            Mail. Der Publisher kommt aus der Kennung im Bestell-Link. Webinare mit Nachfass zählen ab dem Webinar, dessen Nachfass
            am {tag(data.daten_ab)} als erster rausging. {GLOSSAR.nachfass}. {vorlaeufigSatz(data.summe.anfos_vorlaeufig)}
          </Hinweis>
        </Box>
      )}
    </ChartCard>
  );
};

export default NachfassKarte;
